import { Input } from '~/components/ui/input';
import { cn } from '~/lib/utils';

type Props = {
  value: number | null;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
};

/** Tighter stepper (h-7) tuned for a dense toolbar — narrower input, no gap.
 *  A `null` value means "no grid yet": shows a dash, and `−` is locked. `+`
 *  stays active so the user can create the first divider in one click. */
export function CompactStepper({
  value,
  onChange,
  min = 1,
  max = 99,
  disabled = false,
  className,
  ariaLabel,
}: Props) {
  const isEmpty = value === null;
  const display = isEmpty ? '' : value;
  const decDisabled = disabled || isEmpty || value <= min;
  const incDisabled = disabled || (!isEmpty && value >= max);
  return (
    <div className={cn('flex h-7 items-stretch overflow-hidden rounded-md border', className)}>
      <button
        type="button"
        aria-label={`${ariaLabel ?? 'value'} decrement`}
        className="px-1.5 text-sm hover:bg-accent disabled:opacity-40"
        onClick={() => !isEmpty && onChange(Math.max(min, value - 1))}
        disabled={decDisabled}
      >
        −
      </button>
      <Input
        aria-label={ariaLabel}
        type="number"
        value={display}
        placeholder="—"
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => {
          const n = Number(e.currentTarget.value);
          if (Number.isFinite(n) && n >= min) onChange(Math.min(max, n));
        }}
        className="h-full w-10 rounded-none border-x border-y-0 px-0 text-center text-xs shadow-none focus-visible:ring-0"
      />
      <button
        type="button"
        aria-label={`${ariaLabel ?? 'value'} increment`}
        className="px-1.5 text-sm hover:bg-accent disabled:opacity-40"
        onClick={() => onChange(isEmpty ? min + 1 : Math.min(max, value + 1))}
        disabled={incDisabled}
      >
        +
      </button>
    </div>
  );
}
